import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type VoteType = 'upvote' | 'downvote';

interface Vote {
  id: string;
  recipe_id: string;
  user_id: string;
  vote_type: VoteType;
}

export function useRecipeVote(recipeId: string) {
  return useQuery({
    queryKey: ['recipe-vote', recipeId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('recipe_votes')
        .select('*')
        .eq('recipe_id', recipeId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data as Vote | null;
    },
  });
}

export function useVoteOnRecipe() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ recipeId, voteType }: { recipeId: string; voteType: VoteType }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Must be logged in to vote');

      // Check for existing vote
      const { data: existingVote } = await supabase
        .from('recipe_votes')
        .select('*')
        .eq('recipe_id', recipeId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingVote) {
        if (existingVote.vote_type === voteType) {
          // Remove vote if clicking same button
          const { error } = await supabase
            .from('recipe_votes')
            .delete()
            .eq('id', existingVote.id);
          if (error) throw error;
          return { action: 'removed', voteType };
        } else {
          // Change vote type
          const { error } = await supabase
            .from('recipe_votes')
            .update({ vote_type: voteType })
            .eq('id', existingVote.id);
          if (error) throw error;
          return { action: 'changed', voteType };
        }
      } else {
        // Create new vote
        const { error } = await supabase
          .from('recipe_votes')
          .insert({
            recipe_id: recipeId,
            user_id: user.id,
            vote_type: voteType,
          });
        if (error) throw error;
        return { action: 'added', voteType };
      }
    },
    onSuccess: (result, { recipeId }) => {
      queryClient.invalidateQueries({ queryKey: ['recipe-vote', recipeId] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['recipe'] });
      
      const messages = {
        upvote: {
          added: 'Marked as working!',
          removed: 'Vote removed',
          changed: 'Changed to working!',
        },
        downvote: {
          added: 'Reported as not working',
          removed: 'Report removed',
          changed: 'Reported as not working',
        },
      };
      
      toast({
        title: messages[result.voteType][result.action],
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
